// © 2026 AttainIP Engineering

/**
 * API Client with automatic token refresh
 */

import { API_CONFIG } from '../config/api';
import { useAuthStore } from '../store/auth';
import { useTrackingStore } from '../store/tracking';
import { restoreSessionFromRefreshToken } from '../auth/restoreSession';

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

interface ApiError {
  code: string;
  message: string;
  correlationId?: string;
  supportCode?: string;
  /**
   * The refusal body as the server sent it, so a caller can read the FACTS a
   * refusal carries beyond `code`/`message`.
   *
   * ent-api's guest surface is why this exists (RFC-0510 W8): its
   * `guest_owns_cases` refusal names the cases (`cases: [{case_id, name}]`)
   * and `ownership_unverified` names why it could not tell (`reason`). Dropping
   * those on the floor here would turn "they still own Acme v. Beta and two
   * others" into "could not revoke", which is the difference between an
   * actionable refusal and a dead end.
   */
  detail?: Record<string, unknown>;
}

/**
 * FastAPI's default HTTPException handler wraps `detail` as-is under a
 * top-level `detail` key. ss-api's own error convention (RFC-0025 `ErrorDetail`
 * and the ad hoc `{code, message}` dicts used by e.g. `require_org_admin`)
 * raises `HTTPException(detail={"code": ..., "message": ...})`, so the wire
 * shape is `{"detail": {"code": ..., "message": ...}}`, not a flat
 * `{code, message}`. Unwrap that shape when present; otherwise assume the
 * body already is the flat shape (some endpoints return it directly).
 *
 * Core's RFC-0170 `support_code` rides through on the SS-API `details`
 * passthrough (`ErrorDetail.details` == the raw upstream body), so pull it
 * out here and surface it alongside `correlationId`.
 */
function extractApiError(body: unknown): ApiError {
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (detail && typeof detail === 'object' && 'code' in detail && 'message' in detail) {
    const d = detail as ApiError & { details?: { support_code?: unknown } };
    const supportCode =
      typeof d.details?.support_code === 'string' ? d.details.support_code : undefined;
    return { ...d, supportCode, detail: d as unknown as Record<string, unknown> };
  }
  return { ...(body as ApiError), detail: (body ?? undefined) as Record<string, unknown> | undefined };
}

/**
 * Handle 401 response by refreshing token and retrying request.
 *
 * Routes through `restoreSessionFromRefreshToken` (shared, in-flight-deduped)
 * so concurrent 401s from multiple in-flight requests collapse into a single
 * refresh attempt. On any non-ok result the user is signed out and redirected
 * to login — see RFC-0202 §"Session-Loss UX".
 */
async function handleUnauthorized<T>(
  retryFn: () => Promise<T>
): Promise<T | null> {
  const result = await restoreSessionFromRefreshToken({ force: true });
  if (!result.ok) {
    useAuthStore.getState().logout();
    window.location.href = import.meta.env.BASE_URL + 'login?reason=session-expired';
    return null;
  }
  return await retryFn();
}

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl = API_CONFIG.baseUrl) {
    this.baseUrl = baseUrl;
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { skipAuth = false, ...fetchOptions } = options;

    const headers = new Headers(fetchOptions.headers);
    headers.set('Content-Type', 'application/json');

    // Add Bearer token from auth store
    if (!skipAuth) {
      const { accessToken } = useAuthStore.getState();
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
    }

    // Add tracking context header
    const trackingContext = useTrackingStore.getState().getTrackingContext();
    headers.set('X-Tracking-Context', JSON.stringify(trackingContext));

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...fetchOptions,
      headers,
      credentials: 'include', // Include httpOnly cookies
    });

    // Handle token refresh on 401 — only when token is actually expired.
    // If the token is fresh, a 401 means the API can't validate it (config issue),
    // not that we need to refresh. Don't log the user out for that.
    if (response.status === 401 && !skipAuth) {
      const { isTokenExpired } = useAuthStore.getState();
      if (isTokenExpired()) {
        const result = await handleUnauthorized(() => this.request<T>(endpoint, options));
        if (result === null) {
          throw new ApiRequestError('SESSION_EXPIRED', 'Session expired');
        }
        return result;
      }
    }

    if (!response.ok) {
      const body: unknown = await response.json().catch(() => ({
        code: 'UNKNOWN_ERROR',
        message: 'An unexpected error occurred',
      }));
      const error = extractApiError(body);
      throw new ApiRequestError(error.code, error.message, error.correlationId, response.status, error.supportCode, error.detail);
    }

    // 204 No Content has no body — return undefined cast as T so callers
    // can `await` without unwrapping a missing payload.
    if (response.status === 204) {
      return undefined as unknown as T;
    }

    return response.json();
  }

  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  async uploadFormData<T>(endpoint: string, formData: FormData, options?: RequestOptions): Promise<T> {
    const { skipAuth = false, ...fetchOptions } = options || {};

    const headers = new Headers(fetchOptions.headers);

    // Add Bearer token from auth store
    if (!skipAuth) {
      const { accessToken } = useAuthStore.getState();
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
    }

    // Add tracking context header
    const trackingContext = useTrackingStore.getState().getTrackingContext();
    headers.set('X-Tracking-Context', JSON.stringify(trackingContext));

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...fetchOptions,
      method: 'POST',
      body: formData,
      headers,
      credentials: 'include',
    });

    // Handle token refresh on 401
    if (response.status === 401 && !skipAuth) {
      const result = await handleUnauthorized(() => this.uploadFormData<T>(endpoint, formData, options));
      if (result === null) {
        throw new ApiRequestError('SESSION_EXPIRED', 'Session expired');
      }
      return result;
    }

    if (!response.ok) {
      const body: unknown = await response.json().catch(() => ({
        code: 'UPLOAD_ERROR',
        message: 'File upload failed',
      }));
      const error = extractApiError(body);
      throw new ApiRequestError(error.code, error.message, error.correlationId, response.status, error.supportCode, error.detail);
    }

    return response.json();
  }
}

export class ApiRequestError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly correlationId?: string,
    public readonly status?: number,
    // RFC-0170: the code a user quotes to support; correlates one-to-one with
    // a server-side log record. Surface it wherever `correlationId` is shown.
    public readonly supportCode?: string,
    // The refusal body verbatim — see `ApiError.detail`. Undefined when the
    // failure produced no parseable body.
    public readonly detail?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export const apiClient = new ApiClient();

/**
 * The ENTERPRISE API (`ent-api`, RFC-0501) — a second base URL, not a second
 * client. Same bearer token, same refresh-on-401, same refusal unwrapping;
 * only the origin differs, which is exactly what `ApiClient`'s constructor
 * parameter is for.
 *
 * ⚠️ ENTERPRISE-FLAVOUR SURFACES ONLY. ent-api's audience gate accepts
 * `aud=enterprise` and nothing else, so a self-service session calling through
 * here is refused 401 by the service. Nothing in the self-service product
 * should reach for this client — see `appConfig.accountType` and the route
 * guards built on it.
 */
export const enterpriseApiClient = new ApiClient(API_CONFIG.enterpriseBaseUrl);

/**
 * Response from POST /v1/users/activate
 */
export interface ActivateUserResponse {
  user_id: string;
  activated: boolean;
  credits_granted: number;
  bonus_credits?: number;
  total_balance: number;
  activation_reason: string;
  is_corporate_domain?: boolean;
}

/**
 * Activate a new user and grant signup credits.
 * POST /v1/users/activate (Self Service API)
 *
 * Idempotent — safe to call multiple times.
 */
export async function activateUser(
  referralCode?: string,
  authMethod?: string,
): Promise<ActivateUserResponse> {
  const body: Record<string, string> = {};
  if (referralCode) body.referral_code = referralCode;
  if (authMethod) body.auth_method = authMethod;
  return apiClient.post<ActivateUserResponse>(
    API_CONFIG.endpoints.activate,
    body,
  );
}
